/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {Component, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {BusinessSettingsModal} from '../business-settings-modal';
import {EntityElementModel} from '../../../../shared/entity-tree/entity-element/models/entity-element.model';
import {InputMessagesService} from '../../../../shared/input/service/input-messages.service';
import {ValidatorFn, Validators} from '@angular/forms';
import {WarningsEnum} from '../../../../shared/warnings/warnings.enum';
import {MandatoryMessageTypeEnum} from '../../../../shared/input/service/mandatory-message-type.enum';
import {InputTextComponent} from '../../../../shared/input/input-text/input-text.component';
import {take} from 'rxjs/operators';
import {timer} from 'rxjs';
import {ShipToPartyModel} from '../../../../api/ms-settings/business/schema/v1/contracts/pool/ship-to-party.model';
import {BusinessSettingsModalState} from '../business-settings-modal.state';
import {InputTypeEnum} from '../../../../shared/input/input-type.enum';
import {InputListModel} from '../input-list.model';

@Component({
    selector: 'app-ship-to-party-modal',
    templateUrl: './ship-to-party-modal.component.html',
    styleUrls: ['../business-settings-modal.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ShipToPartyModalComponent extends BusinessSettingsModal<BusinessSettingsModalState<ShipToPartyModel>> {

    @ViewChild('shipToPartyNameInput') nameInput: InputTextComponent;
    @ViewChild('shipToPartyCodeInput') codeInput: InputTextComponent;

    readonly ENTITY_NAME = 'ship to party';
    readonly EDIT_TITLE = 'Edit ship to party';
    readonly ADD_TITLE = 'Add a ship to party';
    readonly EDIT_SUBMIT_TITLE = 'Edit';
    readonly ADD_SUBMIT_TITLE = 'Add';

    validators: { [key: string]: ValidatorFn[] } = {
        name: [Validators.pattern(/^[\w-/\s\\.(),]{1,100}$/)],
        code: [Validators.pattern(/^\d{8}$/)]
    };

    public nameMessages = new Map<WarningsEnum, string[]>();
    public codeMessages = new Map<WarningsEnum, string[]>();


    constructor(inputMessagesService: InputMessagesService) {
        super(inputMessagesService);
    }

    setEntity(element: EntityElementModel<ShipToPartyModel>): void {
        this.storeState({
            originalElement: element,
            originalValue: element.originalValue,
            currentValue: element.currentValue
        });
        this.onReady.pipe(take(1))
.subscribe(() => {
            timer(0)
.subscribe(() => {
                this.nameInput.setValue(this.state.currentValue.name);
                this.codeInput.setValue(this.state.currentValue.code);
                this.setActiveInputState(element.active, true);
            });
        });
    }

    getInitialData(): void {
    }

    nameInputChange(name: string): void {
        this.inputMessages.mandatoryWarnings(this.nameInput.inputControl, this.nameMessages, MandatoryMessageTypeEnum.SAVE);
        this.inputMessages.invalidInputValueError(this.nameInput.inputControl, this.nameMessages, this.nameInput.label);
        this.nameMessages = new Map(this.nameMessages);
        const errors = this.nameMessages.get(WarningsEnum.ERROR);

        this.storeState({
            currentValue: {
                ...this.state.currentValue,
                name: name
            },
            invalid: {
                ...this.state.invalid,
                name: (name == null || name.length === 0) || (errors != null && errors.length > 0)
            }
        });

        this.setModalValidity();
    }

    codeInputChange(code: string): void {
        this.inputMessages.invalidInputValueError(
                this.codeInput.inputControl,
                this.codeMessages,
                this.codeInput.label,
                '29333277'
        );

        this.codeMessages = new Map(this.codeMessages);

        const errors = this.codeMessages.get(WarningsEnum.ERROR);

        if (errors.length === 0) {
            this.manageAlreadyExistsCode(code, this.ENTITY_NAME, this.codeMessages, this.codeInput);
        }

        this.storeState({
            currentValue: {
                ...this.state.currentValue,
                code: code
            },
            invalid: {
                ...this.state.invalid,
                code: (code == null || code.length === 0) || (errors != null && errors.length > 0)
            }
        });

        this.setModalValidity();
    }

    protected generateInputs(): InputListModel[] {
        return [{
            input: this.nameInput,
            type: InputTypeEnum.TEXT,
            mandatory: true
        }, {
            input: this.codeInput,
            type: InputTypeEnum.TEXT,
            mandatory: true
        }];
    }
}
